import { useState, useEffect, useCallback, useRef } from "react"
import { Building2, Users, Kanban, CheckSquare, Receipt, FileText, Wrench, Package } from "lucide-react"
import { useAiMentions } from "@/hooks/use-ai-mentions"
import type { MentionEntity } from "@/lib/ag-ui-types"

const ENTITY_ICONS: Record<string, React.ElementType> = {
  Company: Building2,
  Contact: Users,
  Deal: Kanban,
  Task: CheckSquare,
  Invoice: Receipt,
  Quote: FileText,
  ServiceTicket: Wrench,
  Product: Package,
}

const ENTITY_COLORS: Record<string, string> = {
  Company: "text-blue-500",
  Contact: "text-emerald-500",
  Deal: "text-blue-500",
  Task: "text-amber-500",
  Invoice: "text-rose-500",
  Quote: "text-cyan-500",
  ServiceTicket: "text-orange-500",
  Product: "text-blue-500",
}

interface AiMentionPopoverProps {
  query: string
  onSelect: (entity: MentionEntity) => void
  onClose: () => void
  position: { bottom: number; left: number }
}

export function AiMentionPopover({ query, onSelect, onClose, position }: AiMentionPopoverProps) {
  const { data, isLoading } = useAiMentions(query)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const listRef = useRef<HTMLDivElement>(null)

  const results = data?.results || []

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0)
  }, [results.length])

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault()
          setSelectedIndex((i) => Math.min(i + 1, results.length - 1))
          break
        case "ArrowUp":
          e.preventDefault()
          setSelectedIndex((i) => Math.max(i - 1, 0))
          break
        case "Enter":
          e.preventDefault()
          if (results[selectedIndex]) {
            onSelect(results[selectedIndex])
          }
          break
        case "Escape":
          e.preventDefault()
          onClose()
          break
      }
    },
    [results, selectedIndex, onSelect, onClose]
  )

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown)
    return () => document.removeEventListener("keydown", handleKeyDown)
  }, [handleKeyDown])

  if (query.length < 2) return null

  return (
    <div
      className="absolute z-50 w-72 rounded-xl border border-border bg-card shadow-lg"
      style={{ bottom: position.bottom, left: position.left }}
      ref={listRef}
    >
      <div className="px-3 py-2 text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
        Wspomnij encję
      </div>

      {isLoading && (
        <div className="px-3 py-4 text-center text-xs text-muted-foreground">Szukam...</div>
      )}

      {!isLoading && results.length === 0 && (
        <div className="px-3 py-4 text-center text-xs text-muted-foreground">Brak wyników dla "{query}"</div>
      )}

      {results.map((entity, index) => {
        const Icon = ENTITY_ICONS[entity.type] || Building2
        const color = ENTITY_COLORS[entity.type] || "text-gray-500"

        return (
          <button
            key={`${entity.type}-${entity.id}`}
            className={`flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors ${
              index === selectedIndex ? "bg-muted/60" : "hover:bg-muted/30"
            }`}
            onClick={() => onSelect(entity)}
            onMouseEnter={() => setSelectedIndex(index)}
          >
            <Icon size={14} className={color} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm text-foreground">{entity.title}</div>
              {entity.subtitle && (
                <div className="truncate text-[11px] text-muted-foreground">{entity.subtitle}</div>
              )}
            </div>
          </button>
        )
      })}
    </div>
  )
}
